import React, { forwardRef, memo, useContext, SVGAttributes } from 'react';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { Scale } from './Types';
import { noTransition, useTicks } from './utils';
import VizContext from './VizContext';
import VizSVGAxisTick from './VizSVGAxisTick';

export interface Props extends Omit<SVGAttributes<SVGGElement>, 'format' | 'scale'> {
  /**
   *
   */
  align?: 'start' | 'end';
  /**
   * Flag for enabling animations.
   */
  animate?: boolean;
  /**
   * Color of the x axis labels.
   */
  fill?: string;
  /**
   * Flips the labels and ticks to the other side of the axis line.
   */
  flip?: boolean;
  /**
   * Formatting function for the labels.
   *
   * @default (v) => v.toString()
   */
  format?: (v: number) => string;
  /**
   * The d3Scale that should be used for scaling the ticks
   * positions.
   */
  scale: Scale;
  /**
   * Color of the x axis lines.
   */
  stroke?: string;
  /**
   * Number of desired tick values to be rendered. The actual
   * number of rendered labels may differ, see d3-scale for
   * more information.
   */
  tickCount?: number;
  /**
   * The distance in pixels that label should be offset from the
   * axis line.
   */
  tickLabelOffset?: number;
  /**
   * The length in pixels of the tick line.
   */
  tickLineLength?: number;
  /**
   * Explicit x axis values to render.
   */
  tickValues?: number[];
  /**
   * Removes the axis line and only renders the tick lines and labels.
   */
  ticksOnly?: boolean;
  /**
   * The lower x value to render the axis line from. If omitted, the
   * first x domain value will be used.
   */
  x0?: number;
  /**
   * The upper x value to render the axis line to. If omitted, the
   * second x domain value will be used.
   */
  x1?: number;
  /**
   * The lower y value to render the axis line from. If omitted, the
   * first y domain value will be used.
   */
  y0?: number;
  /**
   * The upper y value to render the axis line to. If omitted, the
   * last y domain value will be used.
   */
  y1?: number;
}

/**
 * The generic axis component to be rendered along an axis, including the
 * axis line, tick lines and labels according to the axis given.
 */
export const VizSVGAxis = forwardRef<SVGGElement, Props>((props: Props, ref) => {
  const {
    align = 'start',
    animate,
    className,
    fill = 'currentColor',
    flip,
    format = (v: number) => v.toString(),
    scale,
    stroke = 'currentColor',
    strokeWidth,
    tickCount,
    tickLabelOffset = 4,
    tickLineLength = 4,
    tickValues,
    ticksOnly,
    x0,
    x1,
    y0,
    y1,
    ...rest
  } = props;

  const {
    transition,
    xDomain,
    yDomain,
  } = useContext(VizContext);

  const {
    px0,
    px1,
    py0,
    py1,
    r,
    ticks,
  } = useTicks({
    align: align,
    count: tickCount,
    flip: flip,
    scale: scale,
    values: tickValues,
    x0: x0,
    x1: x1,
    y0: y0,
    y1: y1,
  });

  const cos = Math.cos(r + (Math.PI / 2));
  const sin = Math.sin(r + (Math.PI / 2));

  return (
    <g { ...rest }
        className={ classnames('Bitviz__axis', className) }
        ref={ ref }>
      { !ticksOnly && (
        <motion.line
            animate={ { opacity: xDomain.length && yDomain.length && 1 } }
            className="Bitviz__axis-line"
            initial={ { opacity: 0 } }
            stroke={ stroke }
            strokeWidth={ strokeWidth }
            transition={ animate ? transition : noTransition }
            x1={ px0 }
            x2={ px1 }
            y1={ py0 }
            y2={ py1 } />
      ) }

      { ticks.map(([tick, px, py]) => (
        <VizSVGAxisTick
            animate={ animate }
            fill={ fill }
            key={ tick }
            labelOffset={ tickLabelOffset }
            stroke={ stroke }
            transition={ animate ? transition : noTransition }
            x0={ px }
            x1={ px + ((tickLineLength * cos)) }
            y0={ py }
            y1={ py + ((tickLineLength * sin)) }>
          { format(tick) }
        </VizSVGAxisTick>
      )) }
    </g>
  );
});

export default memo(VizSVGAxis);
